/* ─────────────────────────────────────────────────────────────────
   MOBILE APP DEVELOPMENT — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const MOBILE_DEV_MODULES = [
  {
    module: 1,
    title: "Mobile Fundamentals",
    emoji: "📱",
    topics: [
      {
        id: 1,
        topic: "React Native Setup & Core Components",
        description: "Learn how React Native renders to native mobile UI, and get comfortable with its core components.",
        motivation: "Great start! You just built your first real mobile screen — the same components power apps used by millions. 📱",
        resource: {
          name: "React Native Official Docs — Core Components",
          url: "https://reactnative.dev/docs/components-and-apis",
        },
        task: "Build a Profile Card screen using core React Native components.",
        requirements: [
          "Display a profile image, name, bio, and 3 stat counters (posts, followers, following)",
          "Use View, Text, Image and ScrollView appropriately",
          "Add a Follow button using Pressable or TouchableOpacity",
          "Ensure the layout works on both small and large phone screens",
          "Test on an emulator or Expo Go on a physical device",
        ],
      },
      {
        id: 2,
        topic: "Navigation & Screens",
        description: "Learn to move between multiple screens using React Navigation.",
        motivation: "Multi-screen navigation working! Your app finally feels like a real, explorable product. 🧭",
        resource: {
          name: "React Navigation — Getting Started",
          url: "https://reactnavigation.org/docs/getting-started",
        },
        task: "Build a 3-screen app: Home, Details, and Settings, using React Navigation.",
        requirements: [
          "Stack navigator connecting Home → Details",
          "Bottom tab navigator with Home and Settings tabs",
          "Pass data from Home to Details via navigation params",
          "Custom header title and back button behavior",
          "Smooth transitions between all screens",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "State & Data",
    emoji: "🔄",
    topics: [
      {
        id: 3,
        topic: "State Management & Forms",
        description: "Learn to manage component state and build forms with validation in React Native.",
        motivation: "Your app responds and validates input now — that's the difference between a mockup and a real tool. ✅",
        resource: {
          name: "React Native Official Docs — Handling Text Input",
          url: "https://reactnative.dev/docs/handling-text-input",
        },
        task: "Build a Sign Up form screen with validation.",
        requirements: [
          "Fields: Name, Email, Password, Confirm Password",
          "Show inline validation errors (empty fields, invalid email, mismatched passwords)",
          "Disable the submit button until the form is valid",
          "Show a loading state on submit for at least 1 second (simulated)",
          "Show a success message/screen after submission",
        ],
      },
      {
        id: 4,
        topic: "Consuming APIs & Local Storage",
        description: "Learn to fetch data from a REST API and persist data locally on the device.",
        motivation: "Your app talks to the internet and remembers things now — full-stack mobile skills unlocked! 🌐",
        resource: {
          name: "React Native Official Docs — Networking",
          url: "https://reactnative.dev/docs/network",
        },
        task: "Build a Weather App that fetches live data and remembers the user's last searched city.",
        requirements: [
          "Search a city and fetch live weather data from a public API",
          "Display temperature, condition, humidity, and wind speed",
          "Save the last searched city using AsyncStorage",
          "Auto-load the last searched city's weather on app start",
          "Handle loading and error states (invalid city, network failure)",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "Native Features & Polish",
    emoji: "✨",
    topics: [
      {
        id: 5,
        topic: "Device Features — Camera, Location & Notifications",
        description: "Learn to access native device capabilities from a React Native app.",
        motivation: "You just tapped into real hardware — camera, GPS, notifications. This is where mobile apps get powerful. 📸",
        resource: {
          name: "Expo Docs — Camera",
          url: "https://docs.expo.dev/versions/latest/sdk/camera/",
        },
        task: "Add at least 2 native device features to an app of your choice.",
        requirements: [
          "Capture or pick a photo using the device camera or image picker",
          "Fetch and display the user's current location",
          "Request and handle permissions gracefully (denied state handled too)",
          "Show a local push notification triggered by a button press",
          "Document which native modules/packages you used in the README",
        ],
      },
      {
        id: 6,
        topic: "Styling, Animations & Responsive Layouts",
        description: "Learn to polish an app's look and feel with StyleSheet, Flexbox, and simple animations.",
        motivation: "Your app looks and feels professional now — polish is what turns 'it works' into 'I'd actually use this.' 🎨",
        resource: {
          name: "React Native Official Docs — Animations",
          url: "https://reactnative.dev/docs/animations",
        },
        task: "Polish the Weather App (or another earlier task) with proper styling and animation.",
        requirements: [
          "Consistent theme (colors, spacing, typography) using StyleSheet",
          "Responsive layout that adapts to different screen sizes",
          "At least one animated transition (fade-in, slide, or scale) using the Animated API",
          "Dark mode support (or a clearly themed light/dark toggle)",
          "Pull-to-refresh gesture on the main data list/screen",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "Complete Cross-Platform App",
        description: "Apply everything learned throughout the MicroIntern to ship a complete, polished mobile app.",
        motivation: "Outstanding! You built a real, cross-platform app from scratch — that's certificate-worthy. 🎉",
        resource: {
          name: "React Native Official Docs",
          url: "https://reactnative.dev/docs/getting-started",
        },
        task: "Build a complete Expense Tracker or Recipe App (your choice) combining everything from this MicroIntern.",
        requirements: [
          "Multi-screen navigation (list, detail, add/edit forms)",
          "Data persisted locally (AsyncStorage) or via an API",
          "Form validation on all inputs",
          "At least one native device feature integrated (camera, location, or notifications)",
          "Polished, consistent styling with at least one animation",
          "Works correctly on both Android and iOS (via Expo Go or an emulator)",
          "A well-documented README.md with setup instructions and screenshots",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const MOBILE_DEV_TASKS = MOBILE_DEV_MODULES.flatMap((m) => m.topics);
